import type { SelectionChangedEvent } from '@ag-grid-community/core';
import { ListableInfo, type ListableInfoProps } from '@ics-portal/guardian';
import { type ReactNode, useCallback } from 'react';
import { SearchComponentsTable } from '@components/NewComponentCreateRequestTrigger/components/SearchComponentsDialog/components/SearchComponentsTable';
import { NewComponentCreateRequestTrigger } from '@components/NewComponentCreateRequestTrigger/NewComponentsCreateRequestTrigger';
import { type Component } from '@gql/graphql';
import type { Maybe } from '@models/Maybe';
import { useFormHandler } from '@tra/hooks';

interface SearchComponentsForm {
    selectedComponent: Maybe<Component>;
}

export interface SearchComponentsDialogProps {
    closeAfterSubmit?: boolean;
    defaultComponent?: Maybe<Component>;
    onSubmit: (
        data: Partial<SearchComponentsForm>,
        rawData: Partial<SearchComponentsForm>,
    ) => Promise<void>;
}

export default function SearchComponentsDialog(
    props: SearchComponentsDialogProps,
): ReactNode {
    const { closeAfterSubmit, defaultComponent, onSubmit } = props;

    const { watch, setValue } = useFormHandler<SearchComponentsForm>({
        defaultValues: { selectedComponent: defaultComponent },
        onSubmit,
        closeAfterSubmit:
            typeof closeAfterSubmit === 'boolean' ? closeAfterSubmit : true,
    });
    const selectedComponent = watch('selectedComponent');

    const handleRowSelected = useCallback(
        (event: SelectionChangedEvent<Component>) => {
            const selected = event.api.getSelectedRows()[0];
            setValue('selectedComponent', selected, { shouldDirty: true });
        },
        [setValue],
    );

    const listableInfoParams: Partial<ListableInfoProps> = {
        labelClassName: 'text-se70',
        size: 'xs',
        orientation: 'vertical',
    };

    return (
        <div className="flex flex-column align-items-start w-full h-full gap-4">
            <div className="flex flex-column align-items-start w-full">
                <h3>Selected component</h3>
                <ListableInfo
                    label="Vendor"
                    value={selectedComponent?.componentVendor.name ?? '-'}
                    {...listableInfoParams}
                />
                <ListableInfo
                    label="Name"
                    value={selectedComponent?.name ?? '-'}
                    {...listableInfoParams}
                />
                <ListableInfo
                    label="Version"
                    value={selectedComponent?.version ?? '-'}
                    {...listableInfoParams}
                />
            </div>

            <SearchComponentsTable
                onSelectionChanged={handleRowSelected}
                headerSuffix={
                    <NewComponentCreateRequestTrigger
                        searchComponentsDialogProps={props}
                    />
                }
            />
        </div>
    );
}
